import { TextField } from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import styles from '../../styles.module.scss';
import { fr } from 'date-fns/locale';
import { useTranslates } from 'providers/translates';
import { Language } from 'providers/translates/enums';
import { TextFieldWithCustomPlaceholder } from 'components/CreateMemorialQuiltComponents/TextFieldWithCustomPlaceholder/TextFieldWithCustomPlaceholder';

export interface DateInput {
  day: Date | null;
  month: Date | null;
  year: Date | null;
}

type Props = {
  title: string;
  dateInput?: DateInput;
  onChangeDay?: (date: Date | null) => void;
  onChangeMonth?: (date: Date | null) => void;
  onChangeYear?: (date: Date | null) => void;
  /** Optional: year cannot be after this date (e.g. date of passing). */
  maxYearDate?: Date | null;
  /** Optional: year cannot be before this date (e.g. date of birth). */
  minYearDate?: Date | null;
  /** When true, year field shows a required asterisk (HQ birth/death year pattern). */
  yearRequired?: boolean;
};

/**
 * Separate day / month / year pickers (same pattern as Healing Quilt),
 * so users can enter year only without knowing full DOB/DOD.
 */
export const CustomDateInput: React.FC<Props> = ({
  title,
  dateInput,
  onChangeDay,
  onChangeMonth,
  onChangeYear,
  maxYearDate,
  minYearDate,
  yearRequired = false,
}) => {
  const { selectedLang } = useTranslates();
  const isFrench = selectedLang === Language.FRENCH;

  return (
    <div className={styles.details__input}>
      <div className="inputWithTitle">
        <span>{title}</span>
        <div className={styles.date_wrapper} style={{ display: 'flex', gap: '8px' }}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={isFrench ? fr : undefined}>
            <DesktopDatePicker
              inputFormat="dd"
              views={['day']}
              renderInput={(params) => (
                <TextField className="datePickerInput customSvgIconDatePicker customInput" {...params} />
              )}
              value={dateInput?.day ?? null}
              onChange={(date) => onChangeDay?.(date)}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={isFrench ? fr : undefined}>
            <DesktopDatePicker
              inputFormat="MM"
              disableMaskedInput
              views={['month']}
              renderInput={(params) => (
                <TextField className="datePickerInput customSvgIconDatePicker customInput" {...params} />
              )}
              value={dateInput?.month ?? null}
              onChange={(date) => onChangeMonth?.(date)}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={isFrench ? fr : undefined}>
            <DesktopDatePicker
              inputFormat="yyyy"
              views={['year']}
              value={dateInput?.year ?? null}
              onChange={(date) => onChangeYear?.(date)}
              maxDate={maxYearDate || new Date()}
              minDate={minYearDate || undefined}
              renderInput={(params) =>
                yearRequired ? (
                  <TextFieldWithCustomPlaceholder params={params} value={dateInput?.year ?? null} />
                ) : (
                  <TextField className="datePickerInput customSvgIconDatePicker customInput" {...params} />
                )
              }
            />
          </LocalizationProvider>
        </div>
      </div>
    </div>
  );
};
